import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  CalendarDays,
  CheckCircle2,
  FileText,
  Info,
  Settings,
  Target,
  AlertTriangle,
} from "lucide-react";
import { useAppStore } from "@/store/appStore";
import { Drawer } from "@/components/ui/Modal";
import { Button, Tabs, EmptyState } from "@/components/ui";
import { formatRelative } from "@/utils/format";
import { cn } from "@/utils/format";
import type { AppNotification, NotificationKind } from "@/types";
import type { LucideIcon } from "lucide-react";

const kindIcon: Record<NotificationKind, LucideIcon> = {
  deal: Target,
  invoice: FileText,
  meeting: CalendarDays,
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
};

const POLL_MS = 60_000;

function groupLabel(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startYesterday = new Date(startToday);
  startYesterday.setDate(startYesterday.getDate() - 1);
  if (d >= startToday) return "Today";
  if (d >= startYesterday) return "Yesterday";
  return "Earlier";
}

export function NotificationBell({
  onOpen,
  className,
}: {
  onOpen: () => void;
  className?: string;
}) {
  const isAuthenticated = useAppStore((s) => s.isAuthenticated);
  const loadNotifications = useAppStore((s) => s.loadNotifications);
  const unread = useAppStore(
    (s) => s.notifications.filter((n) => !n.read).length
  );
  const prevUnread = useRef(unread);
  const [bump, setBump] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    void loadNotifications();
    const timer = window.setInterval(() => {
      void loadNotifications();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [isAuthenticated, loadNotifications]);

  useEffect(() => {
    if (prevUnread.current !== unread && unread > 0) {
      setBump(true);
      const timer = window.setTimeout(() => setBump(false), 280);
      prevUnread.current = unread;
      return () => window.clearTimeout(timer);
    }
    prevUnread.current = unread;
  }, [unread]);

  const label = unread > 99 ? "99+" : String(unread);

  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label="Notifications"
      className={cn("notification-bell-btn", className)}
      onClick={onOpen}
    >
      <Bell size={18} aria-hidden />
      {unread > 0 ? (
        <span
          className={cn(
            "notification-bell__badge",
            bump && "notification-bell__badge--bump"
          )}
          aria-label={`${unread} unread`}
        >
          {label}
        </span>
      ) : null}
    </Button>
  );
}

export function NotificationPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const notifications = useAppStore((s) => s.notifications);
  const notificationsLoading = useAppStore((s) => s.notificationsLoading);
  const filter = useAppStore((s) => s.filter);
  const setFilter = useAppStore((s) => s.setFilter);
  const markAsRead = useAppStore((s) => s.markAsRead);
  const markAllRead = useAppStore((s) => s.markAllRead);
  const loadNotifications = useAppStore((s) => s.loadNotifications);
  const [tab, setTab] = useState(filter);

  useEffect(() => {
    if (!open) return;
    void loadNotifications();
  }, [open, loadNotifications]);

  const items = useMemo(() => {
    if (tab === "unread") return notifications.filter((n) => !n.read);
    return notifications;
  }, [notifications, tab]);

  const groups = useMemo(() => {
    const order = ["Today", "Yesterday", "Earlier"] as const;
    const map = new Map<string, AppNotification[]>();
    for (const item of items) {
      const key = groupLabel(item.createdAt);
      const list = map.get(key) ?? [];
      list.push(item);
      map.set(key, list);
    }
    return order
      .filter((key) => map.has(key))
      .map((key) => ({ label: key, items: map.get(key)! }));
  }, [items]);

  return (
    <Drawer open={open} title="Notifications" onClose={onClose}>
      <div className="notification-panel">
        <div className="notification-panel__toolbar">
          <div className="notification-panel__tabs">
            <Tabs
              tabs={[
                { id: "all", label: "All" },
                { id: "unread", label: "Unread" },
              ]}
              value={tab}
              onChange={(id) => {
                setTab(id as "all" | "unread");
                setFilter(id as "all" | "unread");
              }}
            />
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              void markAllRead();
            }}
          >
            Mark all read
          </Button>
          <Button
            size="icon"
            variant="ghost"
            aria-label="Notification settings"
            onClick={() => {
              navigate("/system-settings/notifications");
              onClose();
            }}
          >
            <Settings size={16} />
          </Button>
        </div>

        {notificationsLoading && !items.length ? (
          <EmptyState title="Loading…" description="Fetching your notifications." />
        ) : !items.length ? (
          <EmptyState title="You're all caught up" description="No notifications in this filter." />
        ) : (
          groups.map((group) => (
            <div key={group.label} className="notification-group">
              <div className="notification-group__label">{group.label}</div>
              {group.items.map((item) => {
                const Icon = kindIcon[item.kind] ?? Info;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={cn(
                      "notification-item",
                      !item.read && "notification-item--unread"
                    )}
                    onClick={() => {
                      void markAsRead(item.id);
                      if (item.href) {
                        navigate(item.href);
                        onClose();
                      }
                    }}
                  >
                    <span className="notification-item__icon" aria-hidden>
                      <Icon size={15} strokeWidth={2.1} />
                    </span>
                    <span>
                      <div className="notification-item__title">{item.title}</div>
                      <p className="notification-item__body">{item.body}</p>
                      <div className="notification-item__meta">{formatRelative(item.createdAt)}</div>
                    </span>
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>
    </Drawer>
  );
}
